/**
 * @file colors.ts
 * @description Centralized color mappings for badges and status indicators
 * @module constants
 */

import type { ReactNode } from 'react';
import { IconBrandApple, IconBrandWindows, IconBrandAndroid } from '@tabler/icons-react';
import type { LicenseStatus, Platform, UserRole, UserStatus } from '../types';

/**
 * License status badge colors
 */
export const LICENSE_STATUS_COLORS: Record<LicenseStatus, string> = {
  draft: 'gray',
  active: 'green',
  suspended: 'orange',
  expired: 'gray',
  revoked: 'red',
};

/**
 * Platform badge colors
 */
export const PLATFORM_COLORS: Record<Platform, string> = {
  ios: 'dark',
  windows: 'blue',
  android: 'green',
};

/**
 * Platform badge icons
 */
export const PLATFORM_ICONS: Record<Platform, ReactNode> = {
  ios: <IconBrandApple size={14} />,
  windows: <IconBrandWindows size={14} />,
  android: <IconBrandAndroid size={14} />,
};

/**
 * Platform display labels
 */
export const PLATFORM_LABELS: Record<Platform, string> = {
  ios: 'iOS',
  windows: 'Windows',
  android: 'Android',
};

/**
 * User role badge colors
 */
export const USER_ROLE_COLORS: Record<UserRole, string> = {
  admin: 'red',
  team_member: 'blue',
};

/**
 * User role display labels
 */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  team_member: 'Team Member',
};

/**
 * User status badge colors
 */
export const USER_STATUS_COLORS: Record<UserStatus, string> = {
  active: 'green',
  inactive: 'gray',
};

/**
 * User status display labels
 */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

/**
 * Admin action type colors for audit log
 */
export const ACTION_TYPE_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  status_change: 'cyan',
  revoke: 'red',
  suspend: 'orange',
  reactivate: 'teal',
  transfer: 'grape',
  manual_deactivate: 'pink',
  login: 'indigo',
  logout: 'gray',
  activate: 'lime',
  validate: 'teal',
  deactivate: 'pink',
  reactivate_device: 'teal',
};

/**
 * Admin action type labels for audit log
 */
export const ACTION_TYPE_LABELS: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  status_change: 'Status Change',
  revoke: 'Revoke',
  suspend: 'Suspend',
  reactivate: 'Reactivate',
  transfer: 'Transfer',
  manual_deactivate: 'Manual Deactivate',
  login: 'Login',
  logout: 'Logout',
  activate: 'Activate',
  validate: 'Validate',
  deactivate: 'Deactivate',
  reactivate_device: 'Reactivate (Device)',
};

/**
 * Entity type labels for audit log
 */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  license: 'License',
  activation: 'Activation',
  customer: 'Customer',
  product: 'Product',
  user: 'User',
};
